import React, { useState, useEffect } from "react";
import { Box, Typography, Button, useTheme, CircularProgress } from "@mui/material";
import { tokens } from "../../theme";
import axios from "axios";
import Header from "../../components/Header";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthContext';
import { ArrowCircleRightOutlined } from '@mui/icons-material';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { format } from 'date-fns'; // Importar a função format do date-fns
import ptLocale from 'date-fns/locale/pt-BR'; // Importar o locale pt-BR para date-fns

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { userInfo } = useAuth();

  const [horasTrabalhadas, setHorasTrabalhadas] = useState([]);
  const [clienteNomes, setClienteNomes] = useState({});
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(false); // Estado para indicar se está carregando

  useEffect(() => {
    const fetchDespesas = async () => {
      setLoading(true); // Inicia o loading
  
      try {
        // Busca todas as despesas
        const response = await axios.get("https://pf-auditores.ew.r.appspot.com/api/folha-despesas");
        const despesasData = response.data;
        
        // Filtra apenas as despesas pendentes do usuário logado
        const pendentes = despesasData.filter((despesa) => despesa.estado === "Pendente" && despesa.colaboradorId === userInfo.id);
        
        // Mapeia cada despesa pendente para buscar detalhes do cliente e colaborador
        const despesasWithDetails = await Promise.all(pendentes.map(async (despesa) => {
          try {
            const clienteResponse = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/clientes/${despesa.clienteId}`);
            const colaboradorResponse = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/colaboradores/${despesa.aprovador1}`);
  
            let nomeColaborador = '';
            if (colaboradorResponse.data.tipo === 'ADMIN') {
              // Ajuste para exibir primeiro e último nome do aprovador
              const nomeCompleto = colaboradorResponse.data.nome;
              const nomeSplit = nomeCompleto.split(" ");
              const primeiroNome = nomeSplit[0];
              const ultimoNome = nomeSplit[nomeSplit.length - 1];
              nomeColaborador = `${primeiroNome} ${ultimoNome}`;
            }
  
            return {
              ...despesa,
              nomeCliente: clienteResponse.data.nomeCliente,
              nomeColaborador,
            };
          } catch (error) {
            console.error(`Erro ao buscar detalhes para a despesa ${despesa.id}:`, error);
            return {
              ...despesa,
              nomeCliente: 'Erro ao buscar cliente',
              nomeColaborador: ''
            };
          }
        }));
  
        // Define o estado das despesas pendentes com detalhes
        setDespesas(despesasWithDetails);
      } catch (error) {
        console.error("Erro ao buscar despesas:", error);
      } finally {
        setLoading(false); // Finaliza o loading
      }
    };
  
    fetchDespesas();
  }, [userInfo.id]);
  

  useEffect(() => {
    const fetchHorasTrabalhadas = async () => {
      setLoading(true); // Inicia o loading
      try {
        const response = await axios.get("https://pf-auditores.ew.r.appspot.com/api/horas-trabalhadas");
        const pendentes = response.data.filter((item) => item.estado === "Pendente" && item.colaboradorId === userInfo.id);
        setHorasTrabalhadas(pendentes);
        // Buscar nomes dos clientes das horas trabalhadas
        fetchClienteNomes(pendentes.map(item => item.clienteId));
      } catch (error) {
        console.error("Erro ao buscar horas trabalhadas:", error);
      } finally {
        setLoading(false); // Finaliza o loading
      }
    };

    fetchHorasTrabalhadas();
  }, [userInfo.id]);

  useEffect(() => {
    // Atualizar horasTrabalhadas com o nome do cliente assim que clienteNomes for atualizado
    const updatedHorasTrabalhadas = horasTrabalhadas.map(item => ({
      ...item,
      nomeCliente: clienteNomes[item.clienteId] || "Carregando..."
    }));
    setHorasTrabalhadas(updatedHorasTrabalhadas);
  }, [clienteNomes]);

  const fetchClienteNomes = async (clienteIds) => {
    try {
      // Filtrar IDs de cliente únicos e que ainda não foram buscados
      const uniqueClienteIds = clienteIds.filter(id => !clienteNomes[id]);
      const promises = uniqueClienteIds.map(async id => {
        const response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/clientes/${id}`);
        setClienteNomes(prev => ({
          ...prev,
          [id]: response.data.nomeCliente
        }));
      });
      await Promise.all(promises);
    } catch (error) {
      console.error("Erro ao buscar nomes dos clientes:", error);
    }
  };

  // Função para formatar o nome do mês com a primeira letra maiúscula
  const formatarMes = (data) => {
    const mes = format(data, 'MMMM', { locale: ptLocale });
    return mes.charAt(0).toUpperCase() + mes.slice(1);
  };

  const columns = [
    {
      field: "nomeCliente",
      headerName: "Nome do Cliente",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography color={colors.grey[100]}>{params.value}</Typography>
        );
      },
    },
    {
      field: "dia1Data",
      headerName: "Mês",
      flex: 1,
      renderCell: (params) => {
        // Formatando a data para exibir apenas o nome do mês em português com a primeira letra maiúscula
        const formattedDate = formatarMes(new Date(params.value));
        return (
          <Typography color={colors.grey[100]}>{formattedDate}</Typography>
        );
      },
    },
    {
      field: "totalHoras",
      headerName: "Total de Horas",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography color={colors.grey[100]}>{params.value}</Typography>
        );
      },
    },
    {
      field: "edit",
      headerName: "",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ArrowCircleRightOutlined />}
            onClick={() => navigate(`/horas/editar/${params.row.id}`)}
          >
            Ver
          </Button>
        );
      },
    },
  ];

  const columnsDespesas = [
    {
      field: "data",
      headerName: "Data",
      flex: 0.6,
      valueFormatter: (params) => dateFormatter(params.value),
    },
    {
      field: "nomeCliente",
      headerName: "Cliente",
      flex: 1.2,
    },
    {
      field: "tipoDespesa",
      headerName: "Tipo Despesa",
      flex: 0.7,
    },    
    {
      field: "percursoCompleto",
      headerName: "Percurso Completo",
      flex: 1,
    },
    {
      field: "valorTotal",
      headerName: "Valor Total",
      flex: 0.6,
      valueFormatter: (params) => {
        const euroFormatter = new Intl.NumberFormat("pt-PT", {
          style: "currency",
          currency: "EUR",
        });
        return euroFormatter.format(params.value);
      },
    },
    {
      field: "matricula",
      headerName: "Matricula",
      flex: 0.5,
    },
    {
      field: "ficheiro",
      headerName: "Ficheiro",
      flex: 0.5,
      renderCell: (params) => {
        const link = params.row.ficheiro; // Assumindo que o campo "ficheiro" contém o URL do link
        return (
          <a href={`https://pfauditores.com/wp-content/folha-despesas/${link}`} target="_blank" rel="noopener noreferrer">
            {link}
          </a>
        );
      },
    },
    {
      field: "nomeColaborador",
      headerName: "Aprovador",
      flex: 0.7,
      renderCell: (params) => {
        // Ajuste para exibir primeiro e último nome do aprovador
        const nomeCompleto = params.value;
        const nomeSplit = nomeCompleto.split(" ");
        const primeiroNome = nomeSplit[0];
        const ultimoNome = nomeSplit[nomeSplit.length - 1];
        return (
          <Typography color={colors.grey[100]}>{`${primeiroNome} ${ultimoNome}`}</Typography>
        );
      },
    },
    {
      field: "faturado",
      headerName: "Faturado",
      flex: 0.5,
      valueFormatter: (params) => faturadoFormatter(params.value),
    },
    {
      field: "notas",
      headerName: "Notas",
      flex: 1,
    },
    {
      field: "estado",
      headerName: "Estado",
      flex: 0.5,
    },
    // Nova coluna para o botão "Ver"
    {
      field: "verDetalhes",
      headerName: "",
      flex: 0.7,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/despesas/editar/${params.row.id}`)}
        >
          Ver
        </Button>
      ),
    },
  ];

  // Função para formatar a data no formato "dd/MM/yyyy"
  const dateFormatter = (date) => {
    return format(new Date(date), 'dd/MM/yyyy');
  };

  // Função para formatar o campo "faturado"
  const faturadoFormatter = (faturado) => {
    return faturado === true || faturado === 'true' ? 'Sim' : 'Não';
  };

  return (
    <Box m="20px">
      <Header title="Horas Trabalhadas" subtitle="Lista de horas trabalhadas pendentes" />
      <Box
        height="55vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={horasTrabalhadas}
          columns={columns}
          loading={loading && horasTrabalhadas.length === 0} // Mostra loading enquanto carrega e não há linhas
          autoHeight
          pageSize={10} // Quantidade de itens por página
          rowsPerPageOptions={[10, 25, 50]} // Opções de quantidade de itens por página
          localeText={{
            noRowsLabel: loading ? <CircularProgress size={24} /> : 'Nenhum registo encontrado', // Mostra loading ou texto personalizado
          }}
        />
      </Box>

      <Header title="Folhas de Despesas" subtitle="Lista de folhas de despesas pendentes" />
      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid 
          rows={despesas} 
          columns={columnsDespesas}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
